// src/components/Admin/Auth/ResetPasswordForm.js
import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Auth } from "../../../../api/auth";
import { toast, ToastContainer } from "react-toastify";
import { useLocation, useNavigate, Link } from "react-router-dom";
import "./AuthForms.scss";
import { logoEduca } from "../../../../assets";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export function ResetPasswordForm() {
  const [loading, setLoading] = useState(false);
  const token = useQuery().get("token");
  const navigate = useNavigate();
  const authController = new Auth();
  const { t } = useTranslation("auth");

  const getSchema = (t) =>
    Yup.object({
      password: Yup.string()
        .min(6, t("resetPassword.passwordMin"))
        .required(t("resetPassword.required")),
      confirm: Yup.string()
        .oneOf([Yup.ref("password")], t("resetPassword.passwordsMatch"))
        .required(t("resetPassword.required")),
    });

  useEffect(() => {
    if (!token) {
      toast.error(t("resetPassword.invalidToken"));
      navigate("/auth");
    }
  }, [token, navigate, t]);

  const formik = useFormik({
    initialValues: { password: "", confirm: "" },
    validationSchema: getSchema(t),
    onSubmit: async ({ password }) => {
      try {
        setLoading(true);
        await authController.resetPassword(token, password);
        toast.success(t("resetPassword.successMessage"));
        setTimeout(() => navigate("/auth"), 1500);
      } catch (err) {
        toast.error(err.message || t("resetPassword.errorMessage"));
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="auth-container">
      <div className="auth-card">
        <img src={logoEduca.logoE360} alt="Educa360" className="auth-logo" />

        <ToastContainer />
        <form className="auth-form" onSubmit={formik.handleSubmit}>
          <div className="input-group">
            <input
              name="password"
              type="password"
              placeholder={t("resetPassword.passwordPlaceholder")}
              className="input-pill"
              onChange={formik.handleChange}
              value={formik.values.password}
            />
            {formik.touched.password && formik.errors.password && (
              <div className="field-error">{formik.errors.password}</div>
            )}
          </div>

          <div className="input-group">
            <input
              name="confirm"
              type="password"
              placeholder={t("resetPassword.confirmPlaceholder")}
              className="input-pill"
              onChange={formik.handleChange}
              value={formik.values.confirm}
            />
            {formik.touched.confirm && formik.errors.confirm && (
              <div className="field-error">{formik.errors.confirm}</div>
            )}
          </div>

          <button type="submit" className="btn-pill" disabled={loading}>
            {loading ? t("resetPassword.resetting") : t("resetPassword.submitButton")}
          </button>
        </form>

        <div className="auth-switch" style={{ marginTop: "1rem" }}>
          <Link to="/auth" className="link-btn">
            ← {t("resetPassword.backToLogin")}
          </Link>
        </div>
      </div>
    </div>
  );
}
